const EXTRACTION_METHOD_LABELS = {
  pdf_text: 'PDF Text',
  pdf_ocr: 'PDF OCR',
};

export const PKB_SOURCE_DISPLAY_LABELS = {
  prd_upload: 'PRD Upload',
  manual_upload: 'Manual Upload',
  manual: 'Manual',
  jira: 'JIRA',
  figma_frame: 'Figma',
  figma: 'Figma',
  test_case_migration: 'Test case migration',
};

/** localStorage key for one-shot "open this PKB row" after navigating from Dashboard. */
export const PKB_OPEN_SOURCE_REF_STORAGE_KEY = 'pkb_open_source_ref';

export const decodeHtmlEntities = (value) => {
  const input = String(value ?? '');
  if (!input) return '';

  const decodeOnce = (text) => {
    if (typeof document === 'undefined') return text;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  let decoded = input.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\u00a0/g, ' ');
  for (let i = 0; i < 2; i += 1) {
    const next = decodeOnce(decoded);
    if (next === decoded) break;
    decoded = next;
  }

  return decoded;
};

export const formatDocumentText = (value) =>
  decodeHtmlEntities(value)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

export const normalizeStructuredSections = (sections) => {
  if (!sections || typeof sections !== 'object') return {};

  return Object.fromEntries(
    Object.entries(sections).map(([key, val]) => [
      key,
      Array.isArray(val) ? val.map((item) => formatDocumentText(item)) : formatDocumentText(val),
    ])
  );
};

export const getExtractionMethodLabel = (extraction) => {
  const method = extraction?.extraction_method;
  return EXTRACTION_METHOD_LABELS[method] || null;
};

export const getPdfModalExtractionLabel = (extraction) => {
  const canonical = getExtractionMethodLabel(extraction);
  if (canonical) return canonical;
  if (extraction?.document_subtype !== 'pdf') return null;
  if (extraction?.ocr_used) return 'PDF OCR';
  if (Number.isFinite(extraction?.page_count)) return 'PDF Text';
  return null;
};

export const formatPkbSourceLabel = (source) => {
  if (source == null || source === '') return '';
  const key = String(source).trim();
  if (PKB_SOURCE_DISPLAY_LABELS[key]) return PKB_SOURCE_DISPLAY_LABELS[key];
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const getKnowledgeItemTitle = (item) =>
  item?.content?.name || item?.content?.title || 'Untitled';

export const getKnowledgeItemType = (item) => {
  const issueType = item?.content?.metadata?.issue_type;
  if (issueType) return issueType;
  return item?.knowledge_type || 'Feature';
};

export const getModuleKey = (moduleValue) =>
  typeof moduleValue === 'string' ? moduleValue : moduleValue?.module_key;

export const getJiraInfo = (item) => {
  const metadata = item?.content?.metadata || {};
  const rawId = metadata.id;
  const jiraKeyFromId =
    typeof rawId === 'string' && rawId.startsWith('JIRA:') ? rawId.split(':')[1] : null;
  const jiraKey = metadata.jira_issue_key ||
    item?.jira_issue_key ||
    item?.external_key ||
    item?.content?.external_key ||
    jiraKeyFromId ||
    null;
  const jiraUrl = metadata.source_url ||
    item?.content?.source_url ||
    item?.content?.external_url ||
    item?.external_url ||
    null;
  return { jiraKey, jiraUrl };
};

export const getPriorityChipColor = (priority) => {
  if (priority === 'High' || priority === 'Highest') return 'error';
  if (priority === 'Medium') return 'warning';
  return 'default';
};

export const formatSectionTitle = (value) =>
  String(value).replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

export const getVisiblePageNumbers = (currentPage, totalPages, maxVisible = 5) => {
  const safeMaxVisible = Math.max(1, maxVisible);
  const count = Math.min(safeMaxVisible, totalPages);
  const start =
    totalPages <= safeMaxVisible
      ? 1
      : currentPage <= Math.ceil(safeMaxVisible / 2)
        ? 1
        : currentPage >= totalPages - Math.floor(safeMaxVisible / 2)
          ? totalPages - safeMaxVisible + 1
          : currentPage - Math.floor(safeMaxVisible / 2);

  return Array.from({ length: count }, (_, index) => start + index);
};
